import React, { useState, useEffect, createContext } from 'react';
import { useParams, useNavigate, Routes, Route, Link } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Breadcrumbs,
  Drawer,
  List,
  ListItemIcon,
  ListItemButton,
  Divider,
  Alert,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  ListItemText,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CropsIcon from '@mui/icons-material/Agriculture';
import AnimalsIcon from '@mui/icons-material/Pets';
import InventoryIcon from '@mui/icons-material/Inventory';
import WorkersIcon from '@mui/icons-material/People';
import FinanceIcon from '@mui/icons-material/AttachMoney';
import TasksIcon from '@mui/icons-material/Assignment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import WeatherIcon from '@mui/icons-material/WbSunny';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import { supabase } from '../../services/supabaseClient'; // Adjust path if needed
import type { Worker } from '../../types/worker';

// Import farm components (we'll create these next)
import FarmOverview from '../../components/farm/details/FarmOverview';
import FarmCrops from '../../components/farm/details/FarmCrops';
import FarmAnimals from '../../components/farm/details/FarmAnimals';
import FarmInventory from '../../components/farm/details/FarmInventory';
import FarmWorkers from '../../components/farm/details/FarmWorkers';
import FarmFinances from '../../components/farm/details/FarmFinances';
import FarmTasks from '../../components/farm/details/FarmTasks';
import FarmAnalytics from '../../components/farm/details/FarmAnalytics';
import FarmSettings from '../../components/farm/details/FarmSettings';
import FarmWeather from '../../components/farm/details/FarmWeather';
import FarmSmartFarming from '../../components/farm/details/FarmSmartFarming';
import FarmProduce from '../../components/farm/details/FarmProduce';
import WorkerManagement from '../../components/workers/WorkerManagement';
import { getTutorialFarmById, isTutorialFarmId } from '../../components/farm/tutorialData';

const mockWorkers: Worker[] = [
    {
      id: '1',
      name: 'John Doe',
    role: 'manager',
    phoneNumber: '',
    email: '',
    startDate: '',
    wage: 0,
    paymentType: 'monthly',
    status: 'active',
      specialization: ['Crop Management', 'Animal Care'],
    workingHours: { start: '', end: '' },
    leaveDays: 0,
    leaveHistory: [],
    emergencyContact: { name: '', relationship: '', phone: '' },
    notes: '',
    permissions: [],
    attendance: [],
    },
  // ...add more mock workers as needed
];

const TutorialProducePanel = ({ farm }: { farm: any }) => {
  const tutorialProduce = farm?.tutorialModules?.produce;

  if (!tutorialProduce) return null;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        {tutorialProduce.summary}
      </Alert>
      <Grid container spacing={3}>
        {tutorialProduce.harvestTargets.map((target: any) => (
          <Grid item xs={12} md={6} key={target.name}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {target.name}
                </Typography>
                <Chip label={`Target: ${target.target} ${target.unit}`} color="primary" size="small" sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {target.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tutorial Market-Readiness Checklist
            </Typography>
            {tutorialProduce.marketingChecklist.map((item: string) => (
              <Typography key={item} variant="body2" sx={{ mb: 1 }}>
                - {item}
              </Typography>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const TutorialWorkerManagementPanel = ({ farm }: { farm: any }) => {
  const tutorialWorkforce = farm?.tutorialModules?.workforce;
  const tutorialTaskWorkers = farm?.tutorialModules?.tasks?.workers || [];

  if (!tutorialWorkforce) return null;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        {tutorialWorkforce.summary}
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Example Team Roles
            </Typography>
            {tutorialTaskWorkers.map((worker: any) => (
              <Box key={worker.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">{worker.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {worker.role}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {worker.specialization.map((item: string) => (
                    <Chip key={item} label={item} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Tutorial Workforce Playbook
            </Typography>
            {tutorialWorkforce.playbook.map((item: string) => (
              <Typography key={item} variant="body2" sx={{ mb: 1 }}>
                - {item}
              </Typography>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const FarmTasksWrapper = ({ farmId, tutorialFarm }: { farmId?: string; tutorialFarm?: any | null }) => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tutorialFarm && isTutorialFarmId(farmId)) {
      setWorkers(tutorialFarm?.tutorialModules?.tasks?.workers || mockWorkers);
      setLoading(false);
      return;
    }
    if (!farmId) return;
    setLoading(true);
    supabase
      .from('workers')
      .select('*')
      .eq('farm_id', farmId)
      .then(({ data }) => {
        setWorkers(data || []);
        setLoading(false);
      });
  }, [farmId]);

  if (loading) return <div>Loading workers...</div>;

  return (
    <FarmTasks
      farmId={farmId || 'default'}
      workers={workers}
      tutorialMode={Boolean(tutorialFarm)}
      tutorialTasks={tutorialFarm?.tutorialModules?.tasks?.tasks || []}
      tutorialMessage={tutorialFarm?.tutorialModules?.tasks?.summary}
    />
  );
};

const drawerWidth = 240;

const menuItems = [
  { path: '', label: 'Overview', icon: <DashboardIcon /> },
  { path: 'crops', label: 'Crops Management', icon: <CropsIcon /> },
  { path: 'animals', label: 'Livestock Management', icon: <AnimalsIcon /> },
  { path: 'inventory', label: 'Inventory', icon: <InventoryIcon /> },
  { path: 'produce', label: 'Produce', icon: <AgricultureIcon /> },
  { path: 'workers', label: 'Workers', icon: <WorkersIcon /> },
  { path: 'workers-management', label: 'Workers Management', icon: <WorkersIcon /> },
  { path: 'finances', label: 'Finances', icon: <FinanceIcon /> },
  { path: 'tasks', label: 'Tasks', icon: <TasksIcon /> },
  { path: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
  { path: 'weather', label: 'Weather', icon: <WeatherIcon /> },
  { path: 'settings', label: 'Settings', icon: <SettingsIcon /> },
];

// Create a context for the current farm
export const FarmContext = createContext<any>(null);

const FarmDetails: React.FC = () => {
  const { farmId = 'default-farm' } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [farm, setFarm] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isTutorialFarm = isTutorialFarmId(farmId);
  const tutorialFarmProfile = isTutorialFarm ? getTutorialFarmById(farmId) : null;

  // Helper to refresh farm after settings update
  const refreshFarm = async () => {
    if (isTutorialFarm) {
      const tutorialFarm = getTutorialFarmById(farmId);
      setFarm(tutorialFarm);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('farms')
      .select('*')
      .eq('id', farmId)
      .single();
    if (error || !data) {
      setError('Farm not found.');
      setFarm(null);
    } else {
      setFarm({ ...data, imageUrl: data.image_url });
    }
    setLoading(false);
  };

  useEffect(() => {
    let ignore = false;
    const fetchFarm = async () => {
      setLoading(true);
      setError(null);
      if (isTutorialFarm) {
        const tutorialFarm = getTutorialFarmById(farmId);
        if (!tutorialFarm) {
          setError('Tutorial farm not found.');
          setFarm(null);
        } else {
          setFarm(tutorialFarm);
        }
        setLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from('farms')
        .select('*')
        .eq('id', farmId)
        .single();
      if (!ignore) {
        if (error || !data) {
          setError('Farm not found.');
          setFarm(null);
        } else {
          setFarm({ ...data, imageUrl: data.image_url });
        }
        setLoading(false);
      }
    };
    fetchFarm();
    return () => { ignore = true; };
  }, [farmId, isTutorialFarm]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="h6" noWrap component="div">
          Farm Management
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => {
              navigate(`/dashboard/my-farms/${farmId}/${item.path}`);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </div>
  );

  if (loading) return <Box sx={{ p: 4 }}>Loading farm details...</Box>;
  if (error) return <Box sx={{ p: 4, color: 'error.main' }}>{error}</Box>;
  if (!farm) return <Box sx={{ p: 4 }}>No farm data available.</Box>;

  return (
    <FarmContext.Provider value={{ farm, refreshFarm }}>
      <Box sx={{ display: 'flex', gap: { xs: 0, md: 3 }, alignItems: 'flex-start' }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop workspace panel */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: drawerWidth,
            flexShrink: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              position: 'sticky',
              top: 16,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            {drawer}
          </Paper>
        </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2,
            borderRadius: 3,
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 1, color: 'text.secondary' }}
          >
            <Link
              to="/dashboard"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Dashboard
            </Link>
            <Link
              to="/dashboard/my-farms"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              My Farms
            </Link>
            <Typography color="text.primary">Farm Details</Typography>
          </Breadcrumbs>
          <Typography variant="h5" fontWeight={700}>
            {farm.name || 'Farm Workspace'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage farm operations, records, weather, workers, and outputs from one workspace.
          </Typography>
        </Paper>

        {/*
          NOTE: For wide tables in subpages, wrap them like this:
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Table>...</Table>
          </Box>
        */}
        {/* Optional: Show a banner if in tutorial mode */}
        {isTutorialFarm && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="info.main">
              Tutorial Example: You are viewing onboarding data that is kept separate from your real farm records.
            </Typography>
          </Box>
        )}
        <Box sx={{ width: '100%' }}>
          <Routes>
            <Route path="" element={<FarmOverview farm={farm} />} />
            <Route path="crops" element={<FarmCrops farm={farm} />} />
            <Route path="animals" element={<FarmAnimals farm={farm} />} />
            <Route path="inventory" element={<FarmInventory farm={farm} />} />
            <Route path="produce" element={isTutorialFarm ? <TutorialProducePanel farm={farm} /> : <FarmProduce farm={farm} />} />
            <Route path="workers" element={<FarmWorkers farm={farm} />} />
            <Route path="workers-management" element={isTutorialFarm ? <TutorialWorkerManagementPanel farm={farm} /> : <WorkerManagement farmId={farm.id} />} />
            <Route path="finances" element={<FarmFinances farm={farm} />} />
            <Route path="tasks" element={<FarmTasksWrapper farmId={farm.id} tutorialFarm={tutorialFarmProfile} />} />
            <Route path="smart-farming" element={<FarmSmartFarming farm={farm} />} />
            <Route path="analytics" element={<FarmAnalytics farm={farm} />} />
            <Route path="weather" element={<FarmWeather farm={farm} tutorialProfile={tutorialFarmProfile?.tutorialModules?.weather} />} />
            <Route path="settings" element={<FarmSettings farm={farm} />} />
          </Routes>
        </Box>
      </Box>
    </Box>
    </FarmContext.Provider>
  );
};

export default FarmDetails;
